// src/context/AuthContext.tsx
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  Dispatch,
  SetStateAction,
} from 'react';
import type { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import api from '../service/api';
import { Tokens, User } from '../types/types';

interface AuthCtx {
  user: User | null;
  isAuthenticated: boolean;
  isBootstrapping: boolean;
  setTokens: (access: string, refresh: string) => Promise<void>;
  logout: () => void;
  setUser: Dispatch<SetStateAction<User | null>>;
  getAccessToken: () => string | null;
}

const AuthContext = createContext<AuthCtx>({} as AuthCtx);
export const useAuth = () => useContext(AuthContext);

/** ---- Refresh lock p/ evitar corridas ---- */
let refreshPromise: Promise<Tokens> | null = null;
const doRefresh = async (): Promise<Tokens> => {
  if (!refreshPromise) {
    refreshPromise = api
      .post<Tokens>('/auth/refresh', {
        refreshToken: localStorage.getItem('refreshToken'),
      })
      .then(({ data }) => data)
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isBootstrapping, setIsBootstrapping] = useState(true);
  const isAuthenticated = !!user;

  /** ----- aplica/remove Authorization default do axios ----- */
  const applyAccessHeader = (access: string | null) => {
    if (access) {
      api.defaults.headers.common.Authorization = `Bearer ${access}`;
    } else {
      delete api.defaults.headers.common.Authorization;
    }
  };

  /** ----- grava tokens, aplica header e busca /auth/me ----- */
  const setTokens = async (access: string, refresh: string) => {
    try {
      localStorage.setItem('accessToken', access);
      localStorage.setItem('refreshToken', refresh);
      applyAccessHeader(access);

      const { data } = await api.get<User>('/auth/me');
      setUser(data);
    } catch (err) {
      console.error('Auth setTokens /auth/me failed:', err);
      logout();
      throw err;
    }
  };

  /** ----- logout global ----- */
  const logout = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    applyAccessHeader(null);
    setUser(null);
  };

  const getAccessToken = () => localStorage.getItem('accessToken');

  /** ----- boot inicial ----- */
  useEffect(() => {
    (async () => {
      try {
        let access = localStorage.getItem('accessToken');
        const refresh = localStorage.getItem('refreshToken');

        if (!access && refresh) {
          // FAÇA o refresh e grave os tokens corretamente
          const { data: tokens } = await api.post<Tokens>('/auth/refresh', { refreshToken: refresh });
          await setTokens(tokens.accessToken, tokens.refreshToken); // <-- aplica header + /auth/me + setUser
          setIsBootstrapping(false);
          return; // já terminou o boot
        }

        // se já havia access, só aplica header e busca o /auth/me
        applyAccessHeader(access);
        if (access) {
          const { data } = await api.get<User>('/auth/me');
          setUser(data);
        }
      } catch (err) {
        logout();
      } finally {
        setIsBootstrapping(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** ----- Interceptor: 401 TOKEN_EXPIRED → refresh (com lock) ----- */
  useEffect(() => {
    const respId = api.interceptors.response.use(
      (resp: AxiosResponse) => resp,
      async (error: AxiosError) => {
        const status = error.response?.status;
        const data = error.response?.data as any;
        const code = data?.code; // backend: 'TOKEN_EXPIRED' | 'INVALID_TOKEN' | 'NO_TOKEN' | ...

        const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

        // Se não for 401, segue
        if (status !== 401) return Promise.reject(error);

        // Evita loop em /auth/refresh
        if (originalRequest?.url?.includes('/auth/refresh')) {
          logout();
          return Promise.reject(error);
        }

        // Debug: ver o que o backend retornou no 401
        console.warn(`[AuthInterceptor] 401 Detectado. Code: ${code}`, data);

        // Só tenta refresh quando for Expiração ou Token Inválido (tentar recuperar)
        if (code !== 'TOKEN_EXPIRED' && code !== 'INVALID_TOKEN') {
          console.warn('[AuthInterceptor] 401 não recuperável via refresh. Logout forçado.');
          // Exemplos: NO_TOKEN, USER_NOT_FOUND, INVALID_TOKEN_PAYLOAD...
          logout();
          return Promise.reject(error);
        }

        // Evita re-tentar a mesma request infinitamente
        if (originalRequest._retry) {
          logout();
          return Promise.reject(error);
        }
        originalRequest._retry = true;

        try {
          const tokens = await doRefresh(); // lock evita chamadas paralelas
          await setTokens(tokens.accessToken, tokens.refreshToken);

          // Reenvia a requisição original com novo access
          originalRequest.headers = originalRequest.headers ?? {};
          originalRequest.headers.Authorization = `Bearer ${tokens.accessToken}`;
          return api.request(originalRequest);
        } catch (e) {
          logout();
          return Promise.reject(e);
        }
      }
    );

    return () => {
      api.interceptors.response.eject(respId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // registra uma única vez

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        isBootstrapping,
        setTokens,
        logout,
        setUser,
        getAccessToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
